import { Icons, Loading } from '@commenda/kakashi';
import { DEFAULT_AGENT_MODEL_ID } from '@commenda/types';
import {
  AffiliateWorkflowEditorProvider,
  BuilderAgentColumn,
  CanvasRefreshBadge,
  WorkflowBuilderAgentLayout,
  WorkflowBuilderLayoutHelper,
  WorkflowEditorContent,
  WorkflowEditorShell,
  WorkflowEditorToolbar,
} from '@commenda/ui';
import { useCallback, useMemo, useState } from 'react';
import { useAffiliateFormBuilderLinks } from '../affiliate/form-builder';
import {
  AffiliateWorkflowBuilderHelper,
  IAffiliateBuilderFormTab,
} from './affiliate-workflow-builder-helper';
import { AffiliateWorkflowFormTab } from './AffiliateWorkflowFormTab';
import { useAffiliateWorkflowEditor } from './useAffiliateWorkflowEditor';
import { WorkflowBuilderTabs } from './WorkflowBuilderTabs';

/** The affiliate workflow editor: the same shared canvas and builder agent the admin app mounts,
 * laid out as a document-tabbed editor column with the agent as a full-height column beside it.
 * Orchestration only — the tab strip, the agent column and the form pane each own themselves. */
const AffiliateWorkflowEditor = () => {
  const {
    version,
    error,
    firmId,
    conversationId,
    setConversationParam,
    onStartNewConversation,
    onNavigateToVersion,
    refreshVersion,
    isLoading,
  } = useAffiliateWorkflowEditor();
  const [isChatOpen, setIsChatOpen] = useState(true);
  // Tab state is deliberately not in the URL: `setConversationParam` rebuilds the query from
  // scratch, so a tab param would be wiped the moment the agent creates a conversation.
  const [formTab, setFormTab] = useState<IAffiliateBuilderFormTab | null>(null);
  const [activeTabValue, setActiveTabValue] = useState(
    AffiliateWorkflowBuilderHelper.WORKFLOW_TAB_ID
  );

  // What the chat is told the author is looking at. Derived rather than stored so it can never disagree
  // with the tab strip, and read at send time by the panel.
  const openTab = useMemo(
    () =>
      formTab && activeTabValue === formTab.formId
        ? { openTab: 'form', openFormName: formTab.label }
        : { openTab: 'workflow' },
    [formTab, activeTabValue]
  );

  const onCloseFormTab = useCallback(() => {
    setFormTab(null);
    setActiveTabValue(AffiliateWorkflowBuilderHelper.WORKFLOW_TAB_ID);
  }, []);

  // One form tab at a time: viewing another step's form replaces it rather than stacking.
  const onViewForm = useCallback((formId: string, label: string) => {
    setFormTab({ formId, label });
    setActiveTabValue(formId);
  }, []);

  const formBuilderLinks = useAffiliateFormBuilderLinks(firmId, onViewForm);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (error || !version) {
    return (
      <div className="flex h-screen items-center justify-center">
        <span className="txt-small-regular text-text-tertiary">
          Couldn&apos;t load this workflow version. Refresh to try again.
        </span>
      </div>
    );
  }

  return (
    // The provider is what lets the shared property panel show the agent step's
    // document fields here, without widening them for the rest of the app.
    <AffiliateWorkflowEditorProvider>
      <WorkflowEditorShell
        // Remount on a version change so the canvas provider re-reads initialWorkflow.
        key={version.id}
        initialWorkflow={version.definition}
        formBuilderLinks={formBuilderLinks}
        workflowTemplateId={version.workflowTemplateId}
      >
        <WorkflowBuilderAgentLayout
          agentColumn={
            isChatOpen ? (
              <BuilderAgentColumn
                openTab={openTab}
                workflowTemplateVersion={version}
                conversationId={conversationId}
                modelId={DEFAULT_AGENT_MODEL_ID}
                onConversationCreated={setConversationParam}
                onNavigateToVersion={onNavigateToVersion}
                onSelectConversation={setConversationParam}
                onStartNewConversation={onStartNewConversation}
                onClose={() => setIsChatOpen(false)}
              />
            ) : null
          }
        >
          <WorkflowBuilderTabs
            workflowLabel={version.workflowTemplate?.name ?? 'Workflow'}
            formTab={formTab}
            activeTabValue={activeTabValue}
            onSelectTab={setActiveTabValue}
            onCloseFormTab={onCloseFormTab}
          />
          {/* Both panes keep the same box and the inactive one is hidden rather than unmounted,
                so switching tabs never throws away unsaved canvas edits or the viewport. */}
          <div className="relative min-h-0 flex-1">
            <div
              role="tabpanel"
              id={AffiliateWorkflowBuilderHelper.getTabPanelId(
                AffiliateWorkflowBuilderHelper.WORKFLOW_TAB_ID
              )}
              aria-labelledby={AffiliateWorkflowBuilderHelper.getTabId(
                AffiliateWorkflowBuilderHelper.WORKFLOW_TAB_ID
              )}
              hidden={
                activeTabValue !==
                AffiliateWorkflowBuilderHelper.WORKFLOW_TAB_ID
              }
              className="absolute inset-none overflow-hidden rounded-md border border-border-primary bg-bg-primary shadow-xs"
            >
              <WorkflowEditorContent
                rootClassName={
                  WorkflowBuilderLayoutHelper.EDITOR_ROOT_CLASS_NAME
                }
                renderToolbar={(toolbarParams) => (
                  <WorkflowEditorToolbar
                    setWorkflowDefinition={toolbarParams.setWorkflow}
                    workflowDefinition={toolbarParams.workflow}
                    workflowTemplateVersion={version}
                    handlers={toolbarParams.handlers}
                    hasUnsavedChanges={toolbarParams.hasUnsavedChanges}
                    markAsSaved={toolbarParams.markAsSaved}
                    onVersionChanged={refreshVersion}
                    backHref={`/affiliate/${firmId}/workflows`}
                  />
                )}
                renderCanvasOverlay={() => (
                  <div className="pointer-events-auto absolute left-1/2 top-xl z-20 -translate-x-1/2">
                    <CanvasRefreshBadge
                      workflowTemplateVersionId={version.id}
                    />
                  </div>
                )}
              />
            </div>
            {formTab && (
              <div
                role="tabpanel"
                id={AffiliateWorkflowBuilderHelper.getTabPanelId(
                  formTab.formId
                )}
                aria-labelledby={AffiliateWorkflowBuilderHelper.getTabId(
                  formTab.formId
                )}
                hidden={activeTabValue !== formTab.formId}
                className="absolute inset-none overflow-hidden rounded-md border border-border-primary bg-bg-primary shadow-xs"
              >
                <AffiliateWorkflowFormTab
                  formId={formTab.formId}
                  onClose={onCloseFormTab}
                />
              </div>
            )}
            {/* Sits over whichever pane is open, rather than over the canvas: on the form tab
                  the canvas is hidden, and a reopen control hidden with it is a dead end. */}
            {!isChatOpen && (
              <button
                type="button"
                onClick={() => setIsChatOpen(true)}
                className="absolute right-xl top-xl z-20 flex items-center gap-md rounded-full border border-border-primary bg-bg-primary px-xl py-lg shadow-lg transition-shadow hover:shadow-xl"
              >
                <Icons.MessageSquare01
                  aria-hidden="true"
                  className="size-4 text-text-secondary"
                />
                <span className="txt-small-medium text-text-primary">Chat</span>
              </button>
            )}
          </div>
        </WorkflowBuilderAgentLayout>
      </WorkflowEditorShell>
    </AffiliateWorkflowEditorProvider>
  );
};

export default AffiliateWorkflowEditor;
